import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MicIcon, MapPinIcon, ListChecksIcon, HammerIcon, HardHatIcon, TagIcon, SparklesIcon, ShieldCheckIcon, SquareIcon, ChevronLeftIcon, CheckIcon, ChevronRightIcon, TreeDeciduousIcon, ZapIcon, DropletsIcon, Flower2Icon, BoxIcon, MessageCircleIcon, SearchIcon } from 'lucide-react';
import { AssociateButton } from '../components/AssociateButton';
import { useScreenInit } from '../useScreenInit';
import { useLanguage } from '../components/LanguageContext';
const projects = [{
  id: 'find',
  labelKey: 'welcome.find',
  icon: MapPinIcon,
  route: '/list'
}, {
  id: 'list',
  labelKey: 'welcome.list',
  icon: ListChecksIcon,
  route: '/chat',
  intent: 'shopping-list'
}, {
  id: 'diy',
  labelKey: 'welcome.diy',
  icon: HammerIcon,
  route: '/chat',
  intent: 'diy-instructions'
}, {
  id: 'contractor',
  labelKey: 'welcome.contractor',
  icon: HardHatIcon,
  route: '/chat',
  intent: 'contractor-quote'
}, {
  id: 'deals',
  labelKey: 'welcome.deals',
  icon: TagIcon,
  route: '/list',
  iconBg: 'bg-yellow-100',
  iconColor: 'text-yellow-600'
}];
type Message = {
  role: 'user' | 'ai';
  text: string;
};
type ListItem = {
  id: number;
  name: string;
  aisle: string;
  thumb: string;
  checked: boolean;
};
type DealCategory = {
  id: string;
  labelKey: string;
  icon: typeof HammerIcon;
  bg: string;
  iconColor: string;
  count: number;
};
const dealCategories: DealCategory[] = [{
  id: 'tools',
  labelKey: 'deals.tools',
  icon: HammerIcon,
  bg: 'bg-blue-50',
  iconColor: 'text-blue-600',
  count: 12
}, {
  id: 'paint',
  labelKey: 'deals.paint',
  icon: BoxIcon,
  bg: 'bg-rose-50',
  iconColor: 'text-rose-600',
  count: 8
}, {
  id: 'lumber',
  labelKey: 'deals.lumber',
  icon: TreeDeciduousIcon,
  bg: 'bg-amber-50',
  iconColor: 'text-amber-700',
  count: 6
}, {
  id: 'electrical',
  labelKey: 'deals.electrical',
  icon: ZapIcon,
  bg: 'bg-yellow-50',
  iconColor: 'text-yellow-600',
  count: 5
}, {
  id: 'plumbing',
  labelKey: 'deals.plumbing',
  icon: DropletsIcon,
  bg: 'bg-cyan-50',
  iconColor: 'text-cyan-600',
  count: 9
}, {
  id: 'garden',
  labelKey: 'deals.garden',
  icon: Flower2Icon,
  bg: 'bg-emerald-50',
  iconColor: 'text-emerald-600',
  count: 11
}];
type DealProduct = {
  id: number;
  name: string;
  aisle: string;
  thumb: string;
  msrp: number;
  promo: number;
};
const dealProductsByCategory: Record<string, DealProduct[]> = {
  tools: [{
    id: 1,
    name: 'DeWalt 20V Cordless Drill',
    aisle: 'Aisle 21',
    thumb: 'bg-yellow-500',
    msrp: 199.99,
    promo: 139.99
  }, {
    id: 2,
    name: 'Stanley 16oz Claw Hammer',
    aisle: 'Aisle 22',
    thumb: 'bg-zinc-700',
    msrp: 29.99,
    promo: 19.99
  }, {
    id: 3,
    name: '12-Piece Screwdriver Set',
    aisle: 'Aisle 22',
    thumb: 'bg-red-500',
    msrp: 39.99,
    promo: 24.99
  }, {
    id: 4,
    name: 'Circular Saw 7-1/4"',
    aisle: 'Aisle 23',
    thumb: 'bg-orange-500',
    msrp: 159.99,
    promo: 109.99
  }, {
    id: 5,
    name: '25ft Measuring Tape',
    aisle: 'Aisle 22',
    thumb: 'bg-yellow-400',
    msrp: 17.99,
    promo: 9.99
  }],
  paint: [{
    id: 1,
    name: 'Behr Premium Plus Interior 1gal',
    aisle: 'Aisle 15',
    thumb: 'bg-rose-300',
    msrp: 38.99,
    promo: 27.99
  }, {
    id: 2,
    name: 'Pro 3" Angle Paint Brush',
    aisle: 'Aisle 15',
    thumb: 'bg-amber-300',
    msrp: 14.99,
    promo: 8.99
  }, {
    id: 3,
    name: 'Microfiber Roller 9"',
    aisle: 'Aisle 15',
    thumb: 'bg-slate-300',
    msrp: 11.99,
    promo: 6.99
  }, {
    id: 4,
    name: "Painter's Tape 1.88in x 60yd",
    aisle: 'Aisle 15',
    thumb: 'bg-blue-300',
    msrp: 8.99,
    promo: 5.49
  }],
  lumber: [{
    id: 1,
    name: '2x4x8 Pressure Treated',
    aisle: 'Aisle 18',
    thumb: 'bg-amber-700',
    msrp: 9.99,
    promo: 6.99
  }, {
    id: 2,
    name: '4x4x8 Cedar Post',
    aisle: 'Aisle 4',
    thumb: 'bg-amber-800',
    msrp: 24.99,
    promo: 17.99
  }, {
    id: 3,
    name: '5/4 x 6 Composite Decking',
    aisle: 'Aisle 18',
    thumb: 'bg-stone-500',
    msrp: 38.99,
    promo: 28.99
  }, {
    id: 4,
    name: '3/4" Plywood 4x8 Sheet',
    aisle: 'Aisle 17',
    thumb: 'bg-amber-600',
    msrp: 64.99,
    promo: 49.99
  }],
  electrical: [{
    id: 1,
    name: '12/2 Romex Wire 50ft',
    aisle: 'Aisle 28',
    thumb: 'bg-orange-500',
    msrp: 64.99,
    promo: 44.99
  }, {
    id: 2,
    name: 'LED A19 Bulb 4-Pack',
    aisle: 'Aisle 29',
    thumb: 'bg-yellow-200',
    msrp: 18.99,
    promo: 9.99
  }, {
    id: 3,
    name: 'Smart Light Switch',
    aisle: 'Aisle 29',
    thumb: 'bg-slate-200',
    msrp: 39.99,
    promo: 24.99
  }, {
    id: 4,
    name: 'GFCI Outlet 15A',
    aisle: 'Aisle 28',
    thumb: 'bg-zinc-300',
    msrp: 22.99,
    promo: 14.99
  }],
  plumbing: [{
    id: 1,
    name: '1/2" PVC Pipe 10ft',
    aisle: 'Aisle 31',
    thumb: 'bg-slate-100',
    msrp: 7.99,
    promo: 4.99
  }, {
    id: 2,
    name: 'Chrome Bath Faucet',
    aisle: 'Aisle 32',
    thumb: 'bg-zinc-400',
    msrp: 119.99,
    promo: 79.99
  }, {
    id: 3,
    name: 'Wax Toilet Ring',
    aisle: 'Aisle 32',
    thumb: 'bg-amber-200',
    msrp: 4.99,
    promo: 2.99
  }, {
    id: 4,
    name: 'Silicone Caulk Tube',
    aisle: 'Aisle 9',
    thumb: 'bg-slate-200',
    msrp: 9.99,
    promo: 5.99
  }, {
    id: 5,
    name: 'Pipe Wrench 14"',
    aisle: 'Aisle 31',
    thumb: 'bg-red-500',
    msrp: 34.99,
    promo: 22.99
  }],
  garden: [{
    id: 1,
    name: 'Cubic Yard Mulch',
    aisle: 'Aisle 36',
    thumb: 'bg-amber-700',
    msrp: 6.99,
    promo: 3.99
  }, {
    id: 2,
    name: 'Garden Shovel',
    aisle: 'Aisle 35',
    thumb: 'bg-emerald-700',
    msrp: 29.99,
    promo: 17.99
  }, {
    id: 3,
    name: '50ft Garden Hose',
    aisle: 'Aisle 35',
    thumb: 'bg-green-500',
    msrp: 44.99,
    promo: 27.99
  }, {
    id: 4,
    name: 'All-Purpose Fertilizer',
    aisle: 'Aisle 36',
    thumb: 'bg-lime-600',
    msrp: 19.99,
    promo: 12.99
  }, {
    id: 5,
    name: 'Solar Garden Lights 6pk',
    aisle: 'Aisle 36',
    thumb: 'bg-yellow-300',
    msrp: 34.99,
    promo: 19.99
  }]
};
const generatedListItems: ListItem[] = [{
  id: 1,
  name: '4x4 Cedar Posts',
  aisle: 'Aisle 4',
  thumb: 'bg-amber-800',
  checked: false
}, {
  id: 2,
  name: 'Joist Hangers',
  aisle: 'Aisle 12',
  thumb: 'bg-slate-400',
  checked: false
}, {
  id: 3,
  name: '3" Deck Screws',
  aisle: 'Aisle 7',
  thumb: 'bg-zinc-600',
  checked: false
}, {
  id: 4,
  name: 'Pressure Treated Boards',
  aisle: 'Aisle 18',
  thumb: 'bg-amber-600',
  checked: false
}, {
  id: 5,
  name: 'Wood Sealant',
  aisle: 'Aisle 9',
  thumb: 'bg-orange-200',
  checked: false
}];
export function Welcome() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    t
  } = useLanguage();
  const [mode, setMode] = useState<'menu' | 'chat' | 'list' | 'deals' | 'deals-products' | 'find'>('menu');
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [listItems, setListItems] = useState<ListItem[]>(generatedListItems);
  const [selectedDealCategory, setSelectedDealCategory] = useState<DealCategory | null>(null);
  const [chatIntent, setChatIntent] = useState<'shopping-list' | 'contractor'>('shopping-list');
  // Seed the AI greeting once we enter chat mode
  useEffect(() => {
    if (mode === 'chat' && messages.length === 0) {
      // Delay so the menu slide-up finishes first
      const timer = setTimeout(() => {
        setMessages([{
          role: 'ai',
          text: chatIntent === 'contractor' ? t('welcome.aiGreetingContractor') : t('welcome.aiGreeting')
        }]);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [mode, messages.length, t, chatIntent]);
  const handleTalkToAI = (intent: 'shopping-list' | 'contractor' = 'shopping-list') => {
    if (mode === 'chat') return;
    setChatIntent(intent);
    setMode('chat');
    // Auto-start recording once the morph animation has settled
    setTimeout(() => setIsRecording(true), 700);
  };
  const handleBackToMenu = () => {
    setMode('menu');
    setIsRecording(false);
    setMessages([]);
    setListItems(generatedListItems);
    setSelectedDealCategory(null);
    setChatIntent('shopping-list');
  };
  const handleSelectDealCategory = (category: DealCategory) => {
    setSelectedDealCategory(category);
    setMode('deals-products');
  };
  const handleBackToDeals = () => {
    setMode('deals');
    setSelectedDealCategory(null);
  };
  const toggleListItem = (id: number) => {
    setListItems(prev => prev.map(item => item.id === id ? {
      ...item,
      checked: !item.checked
    } : item));
  };
  const handleMicTap = () => {
    if (isRecording) {
      setIsRecording(false);
      if (chatIntent === 'contractor') {
        setMessages(prev => [...prev, {
          role: 'user',
          text: 'I want to add a deck to the back of my house. Roughly 12x16, attached to the back door.'
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: t('welcome.aiConfirmContractor')
          }]);
        }, 900);
      } else {
        // shopping-list flow
        setMessages(prev => [...prev, {
          role: 'user',
          text: "I'm building a 12x16 deck attached to my back door."
        }]);
        setTimeout(() => {
          setMessages(prev => [...prev, {
            role: 'ai',
            text: "Perfect. I'm putting together your shopping list now — give me one second..."
          }]);
          // Transition chat → list in place (no route change)
          setTimeout(() => setMode('list'), 1400);
        }, 900);
      }
    } else {
      setIsRecording(true);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="px-6 pb-6 pt-2 flex flex-col h-full" data-id="element-217">
      {/* TOP / WHITE AREA */}
      <div className="flex-1 flex flex-col relative overflow-hidden" data-id="element-218">
        <AnimatePresence mode="popLayout" data-id="element-219">
          {mode === 'menu' ? <motion.div key="menu" initial={{
          opacity: 1,
          y: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          y: '-120%',
          opacity: 0,
          transition: {
            duration: 0.55,
            ease: [0.7, 0, 0.84, 0]
          }
        }} className="flex flex-col flex-1" data-id="element-220">
              <div className="mt-3 mb-4 text-center" data-id="element-221">
                <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-222">
                  {t('welcome.headline')}
                </h1>
              </div>

              <div className="flex flex-col gap-2" data-id="element-223">
                {projects.map((project, idx) => <motion.button key={project.id} onClick={() => {
              if (project.id === 'deals') {
                setMode('deals');
                return;
              }
              if (project.id === 'list') {
                handleTalkToAI('shopping-list');
                return;
              }
              if (project.id === 'contractor') {
                handleTalkToAI('contractor');
                return;
              }
              if (project.id === 'find') {
                setMode('find');
                return;
              }
              navigate(project.route, {
                state: {
                  intent: (project as any).intent
                }
              });
            }} layoutId={project.id === 'find' ? 'find-search-bar' : undefined} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.03
            }} className="flex items-center gap-4 py-[10px] px-4 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-slate-700 hover:text-blue-600 text-left" data-id="element-224">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${(project as any).iconBg ?? 'bg-blue-50'} ${(project as any).iconColor ?? 'text-blue-600'}`} data-id="element-225">
                      <project.icon size={20} strokeWidth={2} fill={(project as any).iconBg ? 'currentColor' : 'none'} data-id="element-226" />
                    </div>
                    <span className="font-medium text-[15px] flex-1" data-id="element-227">
                      {t(project.labelKey)}
                    </span>
                  </motion.button>)}
              </div>

              <div className="flex-1 flex items-center justify-center gap-1.5" data-id="element-228">
                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-229">
                  <ShieldCheckIcon size={12} strokeWidth={2.5} className="text-blue-400" data-id="element-230" />
                </div>
                <span className="text-[11px] font-medium text-slate-400 tracking-wide" data-id="element-231">
                  {t('welcome.trustBadge')}
                </span>
              </div>
            </motion.div> : mode === 'chat' ? <motion.div key="chat" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0,
          transition: {
            delay: 0.35,
            duration: 0.4
          }
        }} exit={{
          y: '-120%',
          opacity: 0,
          transition: {
            duration: 0.55,
            ease: [0.7, 0, 0.84, 0]
          }
        }} className="flex flex-col flex-1 overflow-y-auto pt-2 pb-4 space-y-3" data-id="element-232">
              <button onClick={handleBackToMenu} className="self-start flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-233">
                <ChevronLeftIcon size={18} data-id="element-234" />
                Back
              </button>
              {messages.map((msg, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-235">
                  <div className={`max-w-[85%] rounded-2xl p-4 ${msg.role === 'user' ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-slate-100 text-slate-900 rounded-tl-sm'}`} data-id="element-236">
                    <p className="text-[15px] leading-relaxed" data-id="element-237">{msg.text}</p>
                  </div>
                </motion.div>)}
              {isRecording && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex justify-end" data-id="element-238">
                  <div className="max-w-[85%] rounded-2xl p-4 bg-blue-600 text-white rounded-tr-sm flex items-center gap-2" data-id="element-239">
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-240"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-241"></span>
                    <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-242"></span>
                  </div>
                </motion.div>}
            </motion.div> : mode === 'deals' ? <motion.div key="deals" initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={selectedDealCategory ? {
          y: '-120%',
          opacity: 0,
          transition: {
            duration: 0.5,
            ease: [0.7, 0, 0.84, 0]
          }
        } : {
          y: '100%',
          opacity: 0,
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="flex flex-col flex-1 overflow-y-auto pt-2 pb-4" data-id="element-243">
              <div className="flex items-center justify-between mb-2" data-id="element-244">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-245">
                  <ChevronLeftIcon size={18} data-id="element-246" />
                  Back
                </button>
              </div>

              <div className="mb-4" data-id="element-247">
                <h2 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-248">
                  {t('welcome.dealsTitle')}
                </h2>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-249">
                  {t('welcome.dealsSubtitle')}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2.5" data-id="element-250">
                {dealCategories.map((category, idx) => <motion.button key={category.id} onClick={() => handleSelectDealCategory(category)} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + idx * 0.05
            }} className="aspect-square flex flex-col items-center justify-center gap-1.5 p-3 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-slate-700 hover:text-blue-600 relative" data-id="element-251">
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold text-yellow-700 bg-yellow-100 border border-yellow-200 rounded-full px-1.5 py-0.5" data-id="element-252">
                      <TagIcon size={9} fill="currentColor" data-id="element-253" />
                      {category.count}
                    </span>
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${category.bg} ${category.iconColor}`} data-id="element-254">
                      <category.icon size={24} strokeWidth={2} data-id="element-255" />
                    </div>
                    <span className="font-semibold text-sm" data-id="element-256">
                      {t(category.labelKey)}
                    </span>
                    <span className="text-[11px] text-slate-400" data-id="element-257">
                      {category.count} {t('deals.dealsCount')}
                    </span>
                  </motion.button>)}
              </div>
            </motion.div> : mode === 'deals-products' && selectedDealCategory ? <motion.div key="deals-products" initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          opacity: 0,
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="flex flex-col flex-1 overflow-y-auto pt-2 pb-4" data-id="element-258">
              <div className="flex items-center justify-between mb-2" data-id="element-259">
                <button onClick={handleBackToDeals} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to categories" data-id="element-260">
                  <ChevronLeftIcon size={18} data-id="element-261" />
                  Back
                </button>
              </div>

              <div className="mb-4 flex items-center gap-3" data-id="element-262">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${selectedDealCategory.bg} ${selectedDealCategory.iconColor}`} data-id="element-263">
                  <selectedDealCategory.icon size={22} strokeWidth={2} data-id="element-264" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-265">
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-266">
                    {t(selectedDealCategory.labelKey)}{' '}
                    {t('deals.productsTitle')}
                  </h2>
                  <p className="text-slate-500 text-xs mt-0.5" data-id="element-267">
                    {(dealProductsByCategory[selectedDealCategory.id] ?? []).length}{' '}
                    {t('deals.dealsCount')}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2" data-id="element-268">
                {(dealProductsByCategory[selectedDealCategory.id] ?? []).map((product, idx) => {
              const pctOff = Math.round((product.msrp - product.promo) / product.msrp * 100);
              return <motion.button key={product.id} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15 + idx * 0.05
              }} onClick={() => navigate('/product')} className="flex items-center gap-3 p-3 rounded-2xl border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:border-blue-200 hover:shadow-md transition-all text-left relative" data-id="element-269">
                        <div className={`w-12 h-12 rounded-xl shrink-0 ${product.thumb} opacity-80`} data-id="element-270" />
                        <div className="flex-1 min-w-0" data-id="element-271">
                          <p className="text-[14px] font-semibold text-slate-900 truncate leading-tight" data-id="element-272">
                            {product.name}
                          </p>
                          <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-273">
                            <MapPinIcon size={10} className="mr-0.5" data-id="element-274" />
                            {product.aisle}
                          </div>
                          <div className="flex items-baseline gap-1.5 mt-1" data-id="element-275">
                            <span className="text-base font-bold text-red-600" data-id="element-276">
                              ${product.promo.toFixed(2)}
                            </span>
                            <span className="text-[11px] text-slate-400 line-through" data-id="element-277">
                              {t('deals.msrp')} ${product.msrp.toFixed(2)}
                            </span>
                          </div>
                        </div>
                        <span className="self-start shrink-0 inline-flex items-center gap-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full px-2 py-0.5" data-id="element-278">
                          {pctOff}% {t('deals.off')}
                        </span>
                      </motion.button>;
            })}
              </div>
            </motion.div> : mode === 'find' ? <motion.div key="find" initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          transition: {
            duration: 0.4
          }
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.3
          }
        }} className="flex flex-col flex-1 overflow-hidden pt-2 pb-4" data-id="element-279">
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-280">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-281">
                  <ChevronLeftIcon size={18} data-id="element-282" />
                  Back
                </button>
              </div>

              {/* Morphing search bar — shares layoutId with the Find tile in menu mode */}
              <motion.div layoutId="find-search-bar" transition={{
            type: 'spring',
            damping: 26,
            stiffness: 220
          }} className="flex items-center gap-2 bg-white border-2 border-blue-100 rounded-2xl pl-3 pr-2 py-2.5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100 transition-all shrink-0 mb-3" data-id="element-283">
                <SearchIcon size={18} className="text-blue-600 shrink-0" data-id="element-284" />
                <input type="text" placeholder={t('list.searchPlaceholder')} className="flex-1 bg-transparent outline-none text-[14px] text-slate-900 placeholder:text-slate-400 py-1 min-w-0" autoFocus data-id="element-285" />
                <motion.button whileTap={{
              scale: 0.95
            }} type="button" onClick={handleTalkToAI} className="w-9 h-9 rounded-xl bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center shrink-0 transition-colors" aria-label="Talk to AI" data-id="element-286">
                  <MicIcon size={18} data-id="element-287" />
                </motion.button>
              </motion.div>

              {/* Shopping list slides up from the bottom into the white area */}
              <motion.div initial={{
            y: '100%',
            opacity: 0
          }} animate={{
            y: 0,
            opacity: 1,
            transition: {
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.15
            }
          }} className="flex-1 overflow-y-auto" data-id="element-288">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1" data-id="element-289">
                  {t('list.sectionTitle')}
                </h2>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden" data-id="element-290">
                  <div className="divide-y divide-slate-100" data-id="element-291">
                    {listItems.map((item, idx) => <motion.div key={item.id} initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.3 + idx * 0.05
                }} className={`px-3 py-2 flex items-center gap-3 transition-colors ${item.checked ? 'bg-slate-50' : 'bg-white'}`} data-id="element-292">
                        <button onClick={() => toggleListItem(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`} data-id="element-293">
                          {item.checked && <CheckIcon size={14} strokeWidth={3} data-id="element-294" />}
                        </button>
                        <div className="flex-1 flex items-center gap-2.5 cursor-pointer min-w-0" onClick={() => navigate('/product')} data-id="element-295">
                          <div className={`w-8 h-8 rounded-md shrink-0 ${item.thumb} opacity-80`} data-id="element-296" />
                          <div className="flex-1 min-w-0" data-id="element-297">
                            <p className={`text-[14px] font-medium truncate leading-tight ${item.checked ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-298">
                              {item.name}
                            </p>
                            <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-299">
                              <MapPinIcon size={10} className="mr-0.5" data-id="element-300" />
                              {item.aisle}
                            </div>
                          </div>
                          <ChevronRightIcon size={18} className="text-slate-300 shrink-0" data-id="element-301" />
                        </div>
                      </motion.div>)}
                  </div>
                </div>
              </motion.div>
            </motion.div> : <motion.div key="list" initial={{
          y: '100%',
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1,
          transition: {
            duration: 0.55,
            ease: [0.16, 1, 0.3, 1]
          }
        }} exit={{
          y: '100%',
          opacity: 0,
          transition: {
            duration: 0.4,
            ease: 'easeIn'
          }
        }} className="flex flex-col flex-1 overflow-y-auto pt-2 pb-4" data-id="element-302">
              <div className="flex items-center justify-between mb-2" data-id="element-303">
                <button onClick={handleBackToMenu} className="flex items-center gap-1 -ml-2 px-2 py-1.5 rounded-full text-slate-600 hover:bg-slate-100 transition-colors text-sm font-medium" aria-label="Back to options" data-id="element-304">
                  <ChevronLeftIcon size={18} data-id="element-305" />
                  Back
                </button>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-306">
                  {listItems.filter(i => i.checked).length}/{listItems.length}
                </span>
              </div>

              {/* Condensed chat summary — the chat box "shrunk" to one line */}
              {messages.some(m => m.role === 'user') && <motion.div layout initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="mb-2 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-3 py-1.5 text-blue-700 text-xs font-medium" data-id="element-307">
                  <MessageCircleIcon size={12} className="shrink-0" data-id="element-308" />
                  <span className="truncate" data-id="element-309">
                    <span className="font-semibold mr-1 text-blue-500" data-id="element-310">
                      {t('welcome.projectSummary')}:
                    </span>
                    {[...messages].reverse().find(m => m.role === 'user')?.text}
                  </span>
                </motion.div>}

              <div className="mb-2" data-id="element-311">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-tight" data-id="element-312">
                  {t('welcome.listTitle')}
                </h2>
                <p className="text-slate-500 text-xs mt-0.5" data-id="element-313">
                  {t('welcome.listSubtitle')}
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_2px_12px_rgba(15,23,42,0.04)] overflow-hidden" data-id="element-314">
                <div className="divide-y divide-slate-100" data-id="element-315">
                  {listItems.map((item, idx) => <motion.div key={item.id} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.15 + idx * 0.05
              }} className={`px-3 py-2 flex items-center gap-3 transition-colors ${item.checked ? 'bg-slate-50' : 'bg-white'}`} data-id="element-316">
                      <button onClick={() => toggleListItem(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors ${item.checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'}`} data-id="element-317">
                        {item.checked && <CheckIcon size={14} strokeWidth={3} data-id="element-318" />}
                      </button>

                      <div className="flex-1 flex items-center gap-2.5 cursor-pointer min-w-0" onClick={() => navigate('/product')} data-id="element-319">
                        <div className={`w-8 h-8 rounded-md shrink-0 ${item.thumb} opacity-80`} data-id="element-320" />
                        <div className="flex-1 min-w-0" data-id="element-321">
                          <p className={`text-[14px] font-medium truncate transition-colors leading-tight ${item.checked ? 'text-slate-400 line-through' : 'text-slate-900'}`} data-id="element-322">
                            {item.name}
                          </p>
                          <div className="flex items-center text-blue-600 text-[11px] font-medium mt-0.5" data-id="element-323">
                            <MapPinIcon size={10} className="mr-0.5" data-id="element-324" />
                            {item.aisle}
                          </div>
                        </div>
                        <ChevronRightIcon size={18} className="text-slate-300 shrink-0" data-id="element-325" />
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* BOTTOM / GRAY AREA */}
      <motion.div layout transition={{
      duration: 0.5,
      ease: 'easeInOut'
    }} className="mt-auto pt-4 -mx-6 -mb-6 px-6 pb-6 border-t border-slate-200 bg-slate-100 space-y-2.5 flex flex-col items-center" data-id="element-326">
        {/* Helper text — crossfades between "Or describe..." and "Tap to speak / Listening" */}
        <div className="h-5 flex items-center justify-center" data-id="element-327">
          <AnimatePresence mode="wait" data-id="element-328">
            {mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' ? <motion.p key="menu-helper" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25
          }} className="text-sm text-slate-500" data-id="element-329">
                {t('welcome.orDescribe')}
              </motion.p> : mode === 'chat' ? <motion.p key="chat-helper" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            delay: 0.2
          }} className="text-sm text-slate-500" data-id="element-330">
                {isRecording ? t('welcome.listening') : t('welcome.tapToSpeak')}
              </motion.p> : <motion.p key="list-helper" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25,
            delay: 0.2
          }} className="text-sm text-slate-500" data-id="element-331">
                {t('welcome.tapToAddMore')}
              </motion.p>}
          </AnimatePresence>
        </div>

        {/* Morphing CTA: rectangular button ↔ circular mic */}
        <div className="relative w-full flex items-center justify-center" data-id="element-332">
          <AnimatePresence data-id="element-333">
            {(mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find') && <motion.span key="fastest-badge" initial={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8,
            transition: {
              duration: 0.2
            }
          }} className="absolute -top-2 right-3 z-20 flex items-center gap-1 bg-blue-700 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md border border-blue-800" data-id="element-334">
                <SparklesIcon size={10} className="text-yellow-300" data-id="element-335" />
                {t('welcome.fastestHelp')}
              </motion.span>}
          </AnimatePresence>

          {/* Mic-pulse ring */}
          {(mode === 'chat' || mode === 'list') && isRecording && <motion.div animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          repeat: Infinity,
          duration: 1.5
        }} className="absolute w-20 h-20 rounded-full bg-blue-400" data-id="element-336" />}

          <motion.button layout layoutId="talk-to-ai-cta" onClick={mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' ? handleTalkToAI : handleMicTap} disabled={false} transition={{
          type: 'spring',
          damping: 26,
          stiffness: 220
        }} className={mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' ? 'relative z-10 w-full flex items-center justify-center gap-2 rounded-2xl py-[22px] px-6 font-semibold text-base min-h-[77px] bg-blue-600 hover:bg-blue-700 text-white transition-colors' : isRecording ? 'relative z-10 w-20 h-20 rounded-full flex items-center justify-center bg-red-500 hover:bg-red-600 text-white shadow-lg transition-colors' : 'relative z-10 w-20 h-20 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition-colors'} data-id="element-337">
            <AnimatePresence mode="wait" data-id="element-338">
              {mode === 'menu' || mode === 'deals' || mode === 'deals-products' || mode === 'find' ? <motion.span key="menu-content" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="flex items-center gap-2" data-id="element-339">
                  <MicIcon size={20} data-id="element-340" />
                  {t('welcome.talkToAI')}
                </motion.span> : isRecording ? <motion.span key="recording-content" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="flex items-center justify-center" data-id="element-341">
                  <SquareIcon size={28} fill="currentColor" data-id="element-342" />
                </motion.span> : <motion.span key="chat-content" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="flex items-center justify-center" data-id="element-343">
                  <MicIcon size={32} data-id="element-344" />
                </motion.span>}
            </AnimatePresence>
          </motion.button>
        </div>

        <div className="w-full" data-id="element-345">
          <AssociateButton idleLabel={t('welcome.requestAssociate')} data-id="element-346" />
        </div>
      </motion.div>
    </motion.div>;
}